import { PublicIdSchemas } from '@idc-forms/id'
import { Permission, StateTransitionPermission } from '@idc-forms/permissions'
import { z } from 'zod'
import { optionalString, requiredString } from '../primitives/string'
import { InstantSchema } from '../primitives/time'
import { V2CursorPaginationSchema } from './http'

/**
 * Contrato de la administración v2 de roles de plataforma. Es el contrato
 * OpenAPI que consume el SDK generado y el editor web de roles.
 *
 * `name` es único GLOBAL (la UI identifica al rol por nombre). `permissions` se
 * valida contra el catálogo agregado de `@idc-forms/permissions`.
 * `statePermissions` referencia estados por id público (`sta_`) y `users` es la
 * relación inversa (user.roles) referenciada por `usr_`; el backend la reconcilia.
 */

/** Permisos de transición sobre un estado concreto del flujo, por id público. */
export const V2AdminRoleStatePermissionSchema = z.object({
  stateId: PublicIdSchemas.state,
  permissions: z.array(z.nativeEnum(StateTransitionPermission))
})

/** Fila del listado: los permisos por estado y los usuarios se resumen a conteos. */
export const V2AdminRoleSchema = z.object({
  id: PublicIdSchemas.role,
  name: z.string().min(1),
  description: z.string(),
  permissions: z.array(z.nativeEnum(Permission)),
  statePermissionsCount: z.number().int().nonnegative(),
  usersCount: z.number().int().nonnegative(),
  active: z.boolean(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema
})

/** Detalle/editor: incluye los permisos por estado y los usuarios asignados. */
export const V2AdminRoleDetailSchema = V2AdminRoleSchema.omit({
  statePermissionsCount: true,
  usersCount: true
}).extend({
  statePermissions: z.array(V2AdminRoleStatePermissionSchema),
  users: z.array(PublicIdSchemas.user)
})

/** Página cursor-paginada del listado (ADR 0019). */
export const V2AdminRolesPageSchema = z.object({
  data: z.array(V2AdminRoleSchema),
  pagination: V2CursorPaginationSchema
})

/** Total y facetas por estado activo/inactivo, con los mismos filtros del listado. */
export const V2AdminRolesSummarySchema = z.object({
  total: z.number().int().nonnegative(),
  active: z.object({
    true: z.number().int().nonnegative(),
    false: z.number().int().nonnegative()
  })
})

/**
 * Alta de rol. `name` se recorta y debe ser único (case-insensitive) entre roles
 * vivos. Los estados se referencian por `sta_` y los usuarios por `usr_`; un id
 * desconocido se rechaza antes de escribir.
 */
export const V2CreateAdminRoleSchema = z.object({
  name: requiredString()
    .transform((value) => value.trim())
    .pipe(requiredString()),
  description: optionalString(),
  permissions: z.array(z.nativeEnum(Permission)).default([]),
  statePermissions: z.array(V2AdminRoleStatePermissionSchema).default([]),
  userIds: z.array(PublicIdSchemas.user).default([]),
  active: z.boolean().optional()
})

/** Edición parcial. `users` sólo se reconcilia cuando `userIds` viaja en el payload. */
export const V2UpdateAdminRoleSchema = V2CreateAdminRoleSchema.partial()

export type V2AdminRoleStatePermission = z.infer<typeof V2AdminRoleStatePermissionSchema>
export type V2AdminRole = z.infer<typeof V2AdminRoleSchema>
export type V2AdminRoleDetail = z.infer<typeof V2AdminRoleDetailSchema>
export type V2AdminRolesPage = z.infer<typeof V2AdminRolesPageSchema>
export type V2AdminRolesSummary = z.infer<typeof V2AdminRolesSummarySchema>
export type V2CreateAdminRoleInput = z.infer<typeof V2CreateAdminRoleSchema>
export type V2UpdateAdminRoleInput = z.infer<typeof V2UpdateAdminRoleSchema>
